import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Mail, MessageCircle, Linkedin, Instagram, Facebook, Youtube } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Footer = () => {
  const [settings, setSettings] = useState({
    brand_name: 'Media Coverage Marketplace',
    support_email: 'support@businessmintpr.com',
    support_whatsapp: ''
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchSettings();
  }, []);

  return (
    <footer className="bg-[#0d0d0d] text-white py-16">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <h3 className="font-playfair text-2xl font-bold mb-4 text-white">{settings.brand_name}</h3>
            <p className="font-manrope text-sm text-[#a0a0a0] max-w-md mb-6">
              Complete PR & media solutions. Digital PR, social media promotions, and combo bundles - all in one platform.
            </p>
            <Link
              to="/marketplace"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope text-xs uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-all duration-300"
            >
              Explore Marketplace
            </Link>
            
            {/* Social Links */}
            <div className="flex gap-3 mt-6">
              <a href="https://www.linkedin.com/company/businessmint" target="_blank" rel="noopener noreferrer" className="p-2 bg-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:bg-[#0077b5] hover:text-white transition-all" aria-label="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/businessmint" target="_blank" rel="noopener noreferrer" className="p-2 bg-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:bg-gradient-to-br hover:from-purple-600 hover:to-pink-500 hover:text-white transition-all" aria-label="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://www.facebook.com/businessmint" target="_blank" rel="noopener noreferrer" className="p-2 bg-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:bg-[#1877f2] hover:text-white transition-all" aria-label="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.youtube.com/@businessmint" target="_blank" rel="noopener noreferrer" className="p-2 bg-[#2a2a2a] rounded-lg text-[#a0a0a0] hover:bg-[#ff0000] hover:text-white transition-all" aria-label="YouTube">
                <Youtube className="w-5 h-5" />
              </a>
            </div>
          </div>
          
          <div>
            <h4 className="font-manrope font-semibold mb-4 text-sm uppercase tracking-widest text-[#D4AF37]">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/marketplace" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Marketplace
                </Link>
              </li>
              <li>
                <Link to="/about" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Contact Us
                </Link>
              </li>
              <li>
                <Link to="/insights" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Insights
                </Link>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-manrope font-semibold mb-4 text-sm uppercase tracking-widest text-[#D4AF37]">For Agencies</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/partner/apply" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Become a Partner
                </Link>
              </li>
              <li>
                <Link to="/services/bundles" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Bulk Discounts
                </Link>
              </li>
              <li>
                <a href="mailto:partners@businessmintpr.com" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Partner Support
                </a>
              </li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-manrope font-semibold mb-4 text-sm uppercase tracking-widest text-[#D4AF37]">Support & Legal</h4>
            <ul className="space-y-3">
              {settings.support_email && (
                <li>
                  <a 
                    href={`mailto:${settings.support_email}`} 
                    className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    {settings.support_email}
                  </a>
                </li>
              )}
              {settings.support_whatsapp && (
                <li>
                  <a 
                    href={`https://wa.me/${settings.support_whatsapp.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300 flex items-center gap-2"
                  >
                    <MessageCircle className="w-4 h-4" />
                    WhatsApp Support
                  </a>
                </li>
              )}
              <li>
                <Link to="/terms" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/refund" className="font-manrope text-sm text-[#a0a0a0] hover:text-white transition-colors duration-300">
                  Refund Policy
                </Link>
              </li>
            </ul>
          </div>
        </div>
        
        <hr className="border-[#333333] mb-8" />
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-manrope text-sm text-[#737373]">
            © {new Date().getFullYear()} businessmintpr.com. All rights reserved.
          </p>
          <p className="font-manrope text-xs text-[#525252]">
            {settings.brand_name} by businessmintpr.com
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
